import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTriangleExclamation, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { CompanyForm } from "../../company/form/CompanyForm";
import { BillingForm } from "../../company/form/BillingForm";
import {
  buildCreateCompanyPayload,
  draftFromKboRow,
  parseRegistrationInput,
  withBillingDefaults,
  VAT_PATTERN,
} from "../../company/companyDraft";
import type { CompanyDraft } from "../../company/companyDraft";
import { checkCompanyExists, createCompany } from "../../../api/company";
import type { CreateCompanyConflict, KboSearchRow } from "../../../api/company";
import { companyFromCreated } from "./destinationCandidates";
import type { Company } from "../../../types/companyTypes";

export interface CreateCompanyStepProps {
  row: KboSearchRow;
  token: string;
  /**
   * The caller's `contacts.id`. MAY BE UNDEFINED — `state.user.user` is not guaranteed
   * to be loaded — and that is a blocking precondition, not something to paper over
   * with "". See `missingContact` below.
   */
  userId: string | number | undefined;
  onCancel: () => void;
  /**
   * Called EXACTLY ONCE, from a submit handler, after the company is committed
   * (201) or found to be already ours (409 + `linkedToCaller: true`).
   * `alreadyLinked` distinguishes the two so the picker can say which happened.
   */
  onCreated: (company: Company, alreadyLinked: boolean) => void;
}

type Step = "company" | "billing";

/** `createCompany` and axios both hang the status off `error.response.status`. */
const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

/**
 * Onboard a register company INLINE, mid-switch, and hand the result straight back
 * to the picker.
 *
 * 🔴 THE COMPANY IS FULLY ONBOARDED — no stub, no partial record, no bespoke
 * onboarding logic. This drives `POST /api/v1/company` exactly as `AddCompanyPage`
 * does, so it writes `companies` + `billing_information` + the caller's
 * `company_contact` + one `company_contact` per admin. A company created here is
 * indistinguishable from one created through the add-company page.
 *
 * That linking is LOAD-BEARING, not a convenience. The old picker made "the user is
 * a contact of both companies" structurally true because the list WAS the
 * membership. Creating the company is what re-establishes that invariant for a
 * company that was not on the list a moment ago.
 *
 * 🔴 EVERY NETWORK CALL IN THIS FILE IS AN EVENT HANDLER — `checkCompanyExists`,
 * `createCompany`, and the `onCreated` that follows. None of them is an effect, and
 * none may become one. "Create, then refetch" as an effect is the textbook loop
 * shape: the guard survives a rejection and the slice's `loading` oscillates. An
 * event handler cannot be re-armed by rendering, by definition — which removes BOTH
 * loop conditions rather than one.
 */
const CreateCompanyStep: React.FC<CreateCompanyStepProps> = ({
  row,
  token,
  userId,
  onCancel,
  onCreated,
}) => {
  const { t } = useTranslation(["management", "AddCompany"]);

  const [draft, setDraft] = useState<CompanyDraft>(() => draftFromKboRow(row));
  const [step, setStep] = useState<Step>("company");
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Someone else already holds this company — a dead end, with a way back. */
  const [raced, setRaced] = useState(false);

  /**
   * Correctness backstop for the double submit, independent of render timing.
   *
   * ⚠️ TWO INDEPENDENT HALVES, ONE HAZARD — and each is individually SUFFICIENT, so
   * the regression pin cannot tell them apart. Measured: with this ref removed but
   * `submitting` still passed to `BillingForm`, the double-click test stays GREEN;
   * with BOTH removed it goes red. Same shape as the `group` memo / primitive-deps
   * pair documented in `CustomerSwitchTransfer.tsx:195-219`.
   *
   * The ref stays because the write is IRREVERSIBLE and the button half depends on
   * React having re-rendered between the two clicks — true in practice, but not a
   * property worth betting a committed row on. If you take one out you are relying
   * entirely on the other: do that deliberately, or not at all.
   */
  const inFlight = useRef(false);

  /**
   * 🔴 NO CONTACT ID → NO CREATE, AND WE SAY SO BEFORE THE FORM IS FILLED.
   *
   * `createCompanyHandler` rejects a falsy `user_id` with a 400 before it does anything
   * else, so submitting without one is a guaranteed failure — and one the user cannot
   * act on, because the error would arrive after they had filled in a billing form.
   * `AddCompanyPage` guards on `userInfo.id` before it submits at all; this path must
   * too. The failure is knowable up front, so it is stated up front.
   *
   * Belt-and-braces: the backend track is separately dropping `user_id` from its
   * required-guard (it derives the contact from the token now), so this may stop being
   * load-bearing — but a missing contact id still means the caller cannot be linked,
   * which is the invariant the whole switch rests on.
   */
  const missingContact = userId === undefined || userId === null || String(userId).trim() === "";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "vatNumber") {
      // Same re-derivation `AddCompanyPage.handleVatNumberChange` does. Without it,
      // editing the VAT leaves `metadataNumber` pointing at the ORIGINAL register
      // row — i.e. the identity the create is keyed by would silently disagree with
      // the number on screen — and a hand-typed VAT could never satisfy VAT_PATTERN.
      const { kboNumber, numberText, vatFormatted } = parseRegistrationInput(value);
      setDraft((prev) => ({
        ...prev,
        vatNumber: vatFormatted || value,
        // IDENTITY (zero-stripped, === companies.id) …
        metadataNumber: kboNumber,
        // … and the two DISPLAY forms, kept in step. Letting `metadataNumberText` go
        // stale here would make the created company render with the number of the
        // register row the user started from rather than the one they typed.
        metadataNumberText: numberText,
        metadataFormattedNumber: vatFormatted,
      }));
      return;
    }
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleProceedToBilling = async () => {
    setError(null);

    if (!draft.vatNumber || !VAT_PATTERN.test(draft.vatNumber)) {
      setError(t("management:customerSwitchInvalidVat"));
      return;
    }
    if (!draft.metadataNumber) {
      setError(t("management:customerSwitchMissingKbo"));
      return;
    }

    setChecking(true);
    try {
      const response = await checkCompanyExists(draft.metadataNumber, token);
      if (response?.exists) {
        // The picker only offered this row because the search said it was NOT in
        // `companies`. A fresh read disagreeing means it was added between the
        // search and now — the same outcome as the POST race, reached earlier.
        setRaced(true);
        return;
      }
      setDraft((prev) => withBillingDefaults(prev));
      setStep("billing");
    } catch (err) {
      // 🔴 MUST NOT SILENTLY PROCEED. Continuing past an unanswered existence check
      // means walking into a POST that can only 409, after the user has filled in a
      // billing form. Stop here and let them retry.
      console.error("Company existence check failed:", err);
      // A 401 is NOT retryable — say so rather than inviting a pointless retry.
      setError(
        statusOf(err) === 401
          ? t("management:customerSwitchSessionExpired")
          : t("management:customerSwitchCompanyCheckError")
      );
    } finally {
      setChecking(false);
    }
  };

  const selectCreated = (companyId: number, alreadyLinked: boolean) => {
    onCreated(
      companyFromCreated({
        companyId,
        name: draft.companyName,
        // The zero-padded form, matching how every other company's `reg_number`
        // arrives from `getAllUserCompanies`.
        regNumber: draft.metadataNumberText || draft.metadataFormattedNumber || draft.vatNumber,
        addressStreet: draft.addressLine1,
        addressNumber: draft.addressNumber,
        addressZip: draft.postalCode,
        addressCity: draft.city,
        addressCountry: draft.country,
      }),
      alreadyLinked
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlight.current) return;
    // Belt-and-braces with the disabled submit: the write is irreversible, so the
    // precondition is enforced at the handler too, not only in the markup.
    if (missingContact) {
      setError(t("management:customerSwitchNoContactId"));
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const response = await createCompany(buildCreateCompanyPayload(draft, userId), token);
      const companyId = Number(response.company_id);
      if (!Number.isFinite(companyId)) {
        // The id is the whole point of the call — it is what the switch selects and
        // what every later request is keyed by. An unusable one is a failure.
        setError(t("management:customerSwitchCompanyCreateFailed"));
        return;
      }
      selectCreated(companyId, false);
    } catch (err) {
      // 🔴 BRANCH ON THE STATUS, never on the message string. `createCompany` sets
      // `error.message` from a switch on the status; matching that text would break
      // the moment the copy changes.
      const status = statusOf(err);
      if (status === 401) {
        // 🔴 RETRYING CANNOT HELP. Before this branch a 401 fell through to "please try
        // again", which sends the user round a loop that can only fail until they sign
        // in again. There is no global 401 interceptor in this repo to catch it.
        setError(t("management:customerSwitchSessionExpired"));
        return;
      }
      if (status === 409) {
        const body = ((err as { response?: { data?: CreateCompanyConflict } })?.response?.data ??
          {}) as CreateCompanyConflict;
        const conflictId = Number(body.company_id ?? draft.metadataNumber);
        if (body.linkedToCaller === true && Number.isFinite(conflictId)) {
          // Already ours. Nothing was lost — select it and let the switch continue.
          selectCreated(conflictId, true);
          return;
        }
        setRaced(true);
        return;
      }
      console.error("Failed to create the company:", err);
      setError(t("management:customerSwitchCompanyCreateFailed"));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  if (raced) {
    return (
      <div>
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300 flex gap-2">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
          <span>{t("management:customerSwitchCompanyRace", { companyName: draft.companyName })}</span>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="mt-3 text-sm font-semibold underline text-primary-500 dark:text-primary-300"
        >
          {t("management:customerSwitchBackToList")}
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-600 dark:text-primary-300 dark:hover:text-white mb-4 transition-colors disabled:opacity-50"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t("management:customerSwitchBackToList")}
      </button>

      <h4 className="font-semibold text-primary-600 dark:text-white mb-1">
        {t("management:customerSwitchAddCompanyTitle")}
      </h4>
      <p className="text-sm text-primary-400 dark:text-primary-500 mb-4">
        {t("management:customerSwitchAddCompanyHelp")}
      </p>

      {missingContact && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300 flex gap-2">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
          <span>{t("management:customerSwitchNoContactId")}</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex gap-2">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="text-primary-600 text-sm">
        {step === "company" ? (
          <CompanyForm
            companyData={draft}
            isEditable
            // `loading` is CompanyForm's only lever for disabling its forward button.
            // Used here for "blocked" as well as "in flight": both mean the user must
            // not advance, and advancing would only fail later and more expensively.
            loading={checking || missingContact}
            onInputChange={handleInputChange}
            onProceedToBilling={handleProceedToBilling}
          />
        ) : (
          <>
            {/* 🔴 Above the submit, and NOT a post-hoc toast: the write is real and
                irreversible, so the warning has to be readable before the click, not
                after it. */}
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300 flex gap-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
              <span>
                {t("management:customerSwitchCreateCompanyWarning", { companyName: draft.companyName })}
              </span>
            </div>
            <BillingForm
              companyData={draft}
              onInputChange={handleInputChange}
              onBack={() => setStep("company")}
              onSubmit={handleSubmit}
              submitting={submitting || missingContact}
            />
            {submitting && (
              <p className="mt-3 text-sm text-primary-400 inline-flex items-center gap-2">
                <FontAwesomeIcon icon={faSpinner} spin />
                {t("management:customerSwitchAddingCompany", { companyName: draft.companyName })}
              </p>
            )}
          </>
        )}
      </div>

      {checking && (
        <p className="mt-3 text-sm text-primary-400 inline-flex items-center gap-2">
          <FontAwesomeIcon icon={faSpinner} spin />
          {t("management:customerSwitchCheckingRegistration")}
        </p>
      )}
    </div>
  );
};

export default CreateCompanyStep;
